import { all, call, put, takeEvery, takeLatest, race, delay, cancelled } from "redux-saga/effects";
import moment from "moment-timezone";
import { PICTREE, ENDPOINT } from "../../constants";
import { getJwtToken, getDataFromSxi } from "api";
import { setPageTopLoader, setPicTreeRegularSeasonEventAreaData } from "actions";

const LAYER_IDS = new Set(["regular_1_l1", "regular_1_l2", "regular_1_l3"]);
const ID_PATTERN = /^[A-Za-z0-9_-]+$/;

const parseRows = (response) => {
  if (Array.isArray(response)) return response;
  const body = response && Object.prototype.hasOwnProperty.call(response, "body") ? response.body : response;
  if (Array.isArray(body)) return body;
  if (typeof body === "string") {
    try {
      const parsed = JSON.parse(body || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }
  return [];
};

const normalizeSelectedEventKeys = (selectedEventKeys) => (
  (Array.isArray(selectedEventKeys) ? selectedEventKeys : [])
    .map((key) => ({
      gameSpaceId: key?.gameSpaceId || key?.game_space_id,
      boostEventId: key?.boostEventId || key?.boost_event_id,
    }))
    .filter((key) => ID_PATTERN.test(key.gameSpaceId || "") && ID_PATTERN.test(key.boostEventId || ""))
    .filter((key, index, keys) => keys.findIndex((candidate) => (
      candidate.gameSpaceId === key.gameSpaceId && candidate.boostEventId === key.boostEventId
    )) === index)
);

const isValidAreaRequest = (selectedEventKeys, areaLayerId, startDate, endDate) => (
  normalizeSelectedEventKeys(selectedEventKeys).length > 0
  && LAYER_IDS.has(areaLayerId)
  && Boolean(startDate)
  && Boolean(endDate)
);

const buildAreaQueryParts = ({ selectedEventKeys, areaLayerId, startUtc, endUtc }) => {
  const selectedEvents = normalizeSelectedEventKeys(selectedEventKeys);
  const layerId = LAYER_IDS.has(areaLayerId) ? areaLayerId : "regular_1_l1";
  const selectedEventPairs = selectedEvents.map(({ gameSpaceId, boostEventId }) => (
    `(be.game_space_id = '${gameSpaceId}' AND be.id = '${boostEventId}')`
  )).join(" OR ");

  const commonCtes = `
      WITH selected_events AS (
        SELECT
          be.id AS boost_event_id,
          be.game_space_id,
          be.start_datetime,
          be.end_datetime,
          cb.id AS capital_budget_id,
          GREATEST(be.start_datetime, '${startUtc}') AS effective_start_utc,
          LEAST(be.end_datetime, '${endUtc}') AS effective_end_utc
        FROM boost_event be
        JOIN capital_budget cb ON cb.boost_event_id = be.id
        WHERE (${selectedEventPairs})
          AND be.start_datetime < '${endUtc}'
          AND be.end_datetime > '${startUtc}'
      ),
      event_targets AS (
        SELECT DISTINCT
          se.boost_event_id,
          se.game_space_id,
          se.start_datetime,
          se.effective_start_utc,
          se.effective_end_utc,
          se.capital_budget_id,
          btal.power_asset_id
        FROM selected_events se
        JOIN boost_target_asset_list btal ON btal.boost_event_id = se.boost_event_id
      ),
      target_flags AS (
        SELECT DISTINCT
          et.boost_event_id,
          et.game_space_id,
          et.start_datetime,
          et.effective_start_utc,
          et.effective_end_utc,
          et.power_asset_id,
          CASE
            WHEN EXISTS (
              SELECT 1
              FROM budget b
              WHERE b.capital_budget_id = et.capital_budget_id
                AND b.power_asset_id = et.power_asset_id
            )
             AND NOT EXISTS (
               SELECT 1
               FROM power_asset_event ppae
               WHERE ppae.game_space_id = et.game_space_id
                 AND ppae.power_asset_id = et.power_asset_id
                 AND ppae.type = 'PHOTOGRAPH'
                 AND ppae.occurred_datetime < et.start_datetime
             )
             AND NOT EXISTS (
               SELECT 1
               FROM budget b
               JOIN budget_event pre_budget_event
                 ON pre_budget_event.budget_id = b.id
                AND pre_budget_event.occurred_datetime < et.start_datetime
               JOIN budget_event_of_reward_granted pre_reward
                 ON pre_reward.id = pre_budget_event.id
               WHERE b.capital_budget_id = et.capital_budget_id
                 AND b.power_asset_id = et.power_asset_id
             )
            THEN 1 ELSE 0
          END AS is_eligible
        FROM event_targets et
        WHERE et.effective_start_utc < et.effective_end_utc
      ),
      eligible_targets AS (
        SELECT DISTINCT boost_event_id, game_space_id, power_asset_id
        FROM target_flags
        WHERE is_eligible = 1
      ),
      area_map_raw AS (
        SELECT DISTINCT
          et.game_space_id,
          et.power_asset_id,
          gfab.game_field_area_id AS area_id,
          COALESCE(gfad.name, gfa.area_code, gfa.id) AS area_name,
          CASE
            WHEN '${layerId}' = 'regular_1_l2' THEN parent_name.name
            WHEN '${layerId}' = 'regular_1_l3' THEN grandparent_name.name
            ELSE NULL
          END AS prefecture_name
        FROM eligible_targets et
        STRAIGHT_JOIN power_asset_game_space_area paga FORCE INDEX (idx_pagsa_game_space_power_asset_area)
          ON paga.game_space_id = et.game_space_id
         AND paga.power_asset_id = et.power_asset_id
        STRAIGHT_JOIN game_space gs ON gs.id = et.game_space_id
        STRAIGHT_JOIN game_field_layer_bind gflb
          ON gflb.game_field_id = gs.game_field_id
         AND gflb.game_field_layer_id = '${layerId}'
        STRAIGHT_JOIN game_field_area_bind gfab
          ON gfab.game_field_layer_id = gflb.game_field_layer_id
         AND gfab.game_field_area_id = paga.area_id
        LEFT JOIN game_field_area gfa ON gfa.id = gfab.game_field_area_id
        LEFT JOIN game_field_area_display_name gfad
          ON gfad.area_id = gfa.id AND gfad.language_code = 'JA'
        LEFT JOIN game_field_area parent_area ON parent_area.id = gfa.superior_area_id
        LEFT JOIN game_field_area_display_name parent_name
          ON parent_name.area_id = parent_area.id AND parent_name.language_code = 'JA'
        LEFT JOIN game_field_area grandparent_area ON grandparent_area.id = parent_area.superior_area_id
        LEFT JOIN game_field_area_display_name grandparent_name
          ON grandparent_name.area_id = grandparent_area.id AND grandparent_name.language_code = 'JA'
      ),
      area_map AS (
        SELECT
          game_space_id,
          power_asset_id,
          area_id,
          MIN(area_name) AS area_name,
          MIN(prefecture_name) AS prefecture_name
        FROM area_map_raw
        GROUP BY game_space_id, power_asset_id, area_id
      ),
      global_registered AS (
        SELECT COUNT(DISTINCT power_asset_id) AS total_registered
        FROM eligible_targets
      ),
      approval_rows AS (
        SELECT DISTINCT
          tf.game_space_id,
          tf.power_asset_id,
          rr.player_id,
          rre.id AS review_event_id,
          tf.is_eligible
        FROM target_flags tf
        JOIN review_request rr
          ON rr.game_space_id = tf.game_space_id
         AND rr.power_asset_id = tf.power_asset_id
        JOIN review_request_event rre
          ON rre.game_space_id = tf.game_space_id
         AND rre.review_request_id = rr.id
         AND rre.type = 'APPROVED'
         AND rre.occurred_datetime >= tf.effective_start_utc
         AND rre.occurred_datetime < tf.effective_end_utc
        JOIN review_request_event_of_approved rreoa ON rreoa.id = rre.id
      ),
      global_approval AS (
        SELECT
          COUNT(DISTINCT CASE WHEN is_eligible = 1 THEN power_asset_id END) AS total_reviewok,
          COUNT(DISTINCT CASE WHEN is_eligible = 1 THEN player_id END) AS total_participants
        FROM approval_rows
      ),
  `;

  const registeredQuery = {
    query: `${commonCtes}
      registered AS (
        SELECT am.area_id, MIN(am.area_name) AS area_name, MIN(am.prefecture_name) AS prefecture_name,
               COUNT(DISTINCT et.power_asset_id) AS registered_count
        FROM eligible_targets et
        JOIN area_map am ON am.game_space_id = et.game_space_id AND am.power_asset_id = et.power_asset_id
        GROUP BY am.area_id
      )
      SELECT r.area_id, r.area_name, r.prefecture_name, r.registered_count, gr.total_registered
      FROM registered r
      CROSS JOIN global_registered gr
      ORDER BY registered_count DESC, area_name ASC;
    `,
    database: "KpiDashboard",
    type: "list",
  };

  const approvalQuery = {
    query: `${commonCtes}
      approved_by_area AS (
        SELECT am.area_id,
               COUNT(DISTINCT CASE WHEN aa.is_eligible = 1 THEN aa.power_asset_id END) AS reviewok_count,
               COUNT(DISTINCT CASE WHEN aa.is_eligible = 1 THEN aa.player_id END) AS participants_count
        FROM area_map am
        JOIN approval_rows aa
          ON aa.game_space_id = am.game_space_id AND aa.power_asset_id = am.power_asset_id
        GROUP BY am.area_id
      )
      SELECT a.area_id, a.reviewok_count, a.participants_count,
             ga.total_reviewok, ga.total_participants
      FROM approved_by_area a
      CROSS JOIN global_approval ga
      ORDER BY a.reviewok_count DESC, a.area_id ASC;
    `,
    database: "KpiDashboard",
    type: "list",
  };

  return { registeredQuery, approvalQuery };
};

const callWithTimeoutAndRetry = function* (fn, args, timeoutMs = 28000) {
  try {
    const result = yield race({ response: call(fn, ...args), timeout: delay(timeoutMs) });
    if (result.timeout) throw new Error(`timeout after ${timeoutMs}ms`);
    return result.response;
  } finally {
    if (yield cancelled()) return null;
  }
};

function* handleEventAreaData(action) {
  const payload = action?.payload || {};
  const selectedEventKeys = normalizeSelectedEventKeys(payload.selectedEventKeys);
  const areaLayerId = payload.selectedAreaLayerId;
  const [startDate, endDate] = payload.selectedDateRange || [];
  const requestKey = [JSON.stringify(selectedEventKeys), areaLayerId, startDate, endDate].join("|");

  if (!selectedEventKeys.length || !LAYER_IDS.has(areaLayerId) || !startDate || !endDate) {
    yield put(setPicTreeRegularSeasonEventAreaData({ areaSummary: null, areaTable: [], areaLoading: false, areaError: null, areaRequestKey: requestKey }));
    return;
  }

  if (!isValidAreaRequest(selectedEventKeys, areaLayerId, startDate, endDate)) {
    yield put(setPicTreeRegularSeasonEventAreaData({ areaSummary: null, areaTable: [], areaLoading: false, areaError: null, areaRequestKey: requestKey }));
    return;
  }

  yield put(setPageTopLoader(true));
  yield put(setPicTreeRegularSeasonEventAreaData({ areaSummary: null, areaTable: [], areaLoading: true, areaError: null, areaRequestKey: requestKey }));
  try {
    const jwtToken = yield call(getJwtToken);
    const startUtc = moment.tz(startDate, "Asia/Tokyo").startOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
    const endUtc = moment.tz(endDate, "Asia/Tokyo").add(1, "day").startOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
    const { registeredQuery, approvalQuery } = buildAreaQueryParts({ selectedEventKeys, areaLayerId, startUtc, endUtc });
    const { registeredResult, approvalResult } = yield all({
      registeredResult: call(callWithTimeoutAndRetry, getDataFromSxi, [registeredQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken]),
      approvalResult: call(callWithTimeoutAndRetry, getDataFromSxi, [approvalQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken]),
    });
    const registeredRows = parseRows(registeredResult);
    const approvalRows = parseRows(approvalResult);
    const approvalByArea = new Map(approvalRows.map((row) => [row.area_id, row]));
    const totalRegistered = Number(registeredRows[0]?.total_registered) || 0;
    const totalReviewOk = Number(approvalRows[0]?.total_reviewok) || 0;
    const totalParticipants = Number(approvalRows[0]?.total_participants) || 0;
    const normalized = registeredRows.map((row, index) => {
      const approval = approvalByArea.get(row.area_id) || {};
      const registeredCount = Number(row.registered_count) || 0;
      const reviewOk = Number(approval.reviewok_count) || 0;
      return {
      areaId: row.area_id,
      name: row.area_name || row.area_id,
      prefectureName: row.prefecture_name || "",
      count: registeredCount,
      unshotCount: registeredCount - reviewOk,
      reviewOk,
      achievedPercent: registeredCount ? Number(((reviewOk / registeredCount) * 100).toFixed(2)) : 0,
      participants: Number(approval.participants_count) || 0,
      index: index + 1,
      totalParticipants,
      };
    });
    const summary = normalized.reduce((resultRow, row) => ({
      registeredAreaCount: resultRow.registeredAreaCount + 1,
      unshotAreaCount: resultRow.unshotAreaCount + (row.reviewOk === 0 ? 1 : 0),
      totalRegistered: totalRegistered || resultRow.totalRegistered,
      totalReviewOk: totalReviewOk || resultRow.totalReviewOk,
      totalParticipants,
    }), { registeredAreaCount: 0, unshotAreaCount: 0, totalRegistered, totalReviewOk, totalParticipants });
    summary.totalAchievedPercent = summary.totalRegistered ? Number(((summary.totalReviewOk / summary.totalRegistered) * 100).toFixed(2)) : 0;
    const table = normalized.map((row) => [
      row.index,
      row.name,
      ...(areaLayerId === "regular_1_l1" ? [] : [row.prefectureName]),
      row.count,
      row.unshotCount,
      row.reviewOk,
      row.achievedPercent,
      row.participants,
    ]);
    yield put(setPicTreeRegularSeasonEventAreaData({ areaSummary: summary, areaTable: table, areaLoading: false, areaError: null, areaRequestKey: requestKey }));
  } catch (error) {
    yield put(setPicTreeRegularSeasonEventAreaData({ areaSummary: null, areaTable: [], areaLoading: false, areaError: "エリア別データの取得に失敗しました。", areaRequestKey: requestKey }));
  } finally {
    yield put(setPageTopLoader(false));
  }
}

function* clearEventAreaData() {
  yield put(setPicTreeRegularSeasonEventAreaData({
    areaSummary: null,
    areaTable: [],
    areaLoading: false,
    areaError: null,
    areaRequestKey: null,
  }));
}

export default function* pictreeRegularSeasonEventAreaSaga() {
  yield takeEvery(PICTREE.GET_REGULAR_SEASON_EVENT_BOOTSTRAP_DATA, clearEventAreaData);
  yield takeLatest(PICTREE.GET_REGULAR_SEASON_EVENT_AREA_DATA, handleEventAreaData);
}

export {
  buildAreaQueryParts,
  isValidAreaRequest,
  normalizeSelectedEventKeys,
  parseRows,
};
